import React, { useEffect, useState } from 'react';
import { Card, Typography, Tag, Modal, Spin, Empty, Alert, Grid } from 'antd';
import { NotificationOutlined } from '@ant-design/icons';
import { useAppSelector } from '../hooks/redux';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import {
  useGetChangelogEntriesQuery,
  useGetChangelogEntryQuery,
  useGetChangelogUnseenCountQuery,
  useMarkChangelogSeenMutation,
} from '../services/changelogApi';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

/**
 * "Novedades" — permanent in-app changelog / light help center. Lists every
 * published entry (newest first); each opens the full manual. Entries never
 * expire: manuals stay consultable forever.
 *
 * The manual HTML ships its own styles, so it renders inside a sandboxed
 * iframe (no scripts): App.css mobile !important rules can't leak in, and the
 * manual's styles can't leak out.
 */
const Novedades: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const resolvedTheme = useAppSelector((state) => state.theme.resolvedTheme);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data: entries, isLoading, isError } = useGetChangelogEntriesQuery();
  const { data: unseen } = useGetChangelogUnseenCountQuery();
  const [markSeen] = useMarkChangelogSeenMutation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: detail, isFetching: detailLoading } = useGetChangelogEntryQuery(selectedId!, {
    skip: !selectedId,
  });

  // Snapshot the unseen count once: the list is sorted newest-first, so the
  // first N cards get the "Nuevo" tag for this visit even after markSeen
  // resets the server-side counter (which live-clears the sidebar badge).
  const [newCount, setNewCount] = useState<number | null>(null);
  useEffect(() => {
    if (unseen !== undefined && newCount === null) {
      setNewCount(unseen.count);
      if (unseen.count > 0) {
        markSeen();
      }
    }
  }, [unseen, newCount, markSeen]);

  const selectedEntry = entries?.find((e) => e.id === selectedId);

  // Build the iframe document. Manuals may be full HTML docs or bare fragments
  // (ours start at <title>/<style>). Either way we ensure a <html data-theme>
  // so their `:root[data-theme]` rules match the app theme, and a viewport meta
  // so they scale on mobile. Manuals that only use prefers-color-scheme still
  // work — the data-theme hint is simply extra.
  // El iframe hereda el color-scheme del documento que lo embebe, así que el
  // manual queda en el tema de la app. Eso solo es seguro si el fondo está
  // dicho: un manual que fija tinta oscura sin fondo quedaba ilegible cuando el
  // lienzo del iframe se volvía oscuro. Esta base va PRIMERO en el head, para
  // que cualquier regla del propio manual la sobrescriba.
  const baseStyle =
    `<style>:root{color-scheme:${resolvedTheme};}` +
    `html,body{background:Canvas;color:CanvasText;}</style>`;

  const buildSrcDoc = (html: string) => {
    const themeAttr = `data-theme="${resolvedTheme}"`;
    if (/<html[\s>]/i.test(html)) {
      const withTheme = html.replace(/<html(?![^>]*data-theme)/i, `<html ${themeAttr}`);
      // Justo después de <head> si lo trae; si no, se le abre uno.
      if (/<head[\s>]/i.test(withTheme)) {
        return withTheme.replace(/(<head[^>]*>)/i, `$1${baseStyle}`);
      }
      return withTheme.replace(/(<html[^>]*>)/i, `$1<head>${baseStyle}</head>`);
    }
    return (
      `<!doctype html><html ${themeAttr}><head><meta charset="utf-8">` +
      `<meta name="viewport" content="width=device-width, initial-scale=1">` +
      baseStyle +
      `</head><body>${html}</body></html>`
    );
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? 12 : 24 }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
          <NotificationOutlined style={{ marginRight: 8, color: 'var(--color-primary)' }} />
          Novedades
        </Title>
        <Text type="secondary">
          Funcionalidades nuevas del sistema y sus manuales de uso. Puedes volver a consultarlas cuando quieras.
        </Text>
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las novedades"
          description="Intenta de nuevo en unos momentos."
        />
      )}

      {entries && entries.length === 0 && (
        <Empty description="Aún no hay novedades publicadas" style={{ padding: 48 }} />
      )}

      {entries?.map((entry, index) => (
        <Card
          key={entry.id}
          hoverable
          onClick={() => setSelectedId(entry.id)}
          style={{ marginBottom: 12 }}
          styles={{ body: { padding: isMobile ? 14 : 20 } }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: isMobile ? 15 : 16 }}>
                  {entry.title}
                </Text>
                {newCount !== null && index < newCount && <Tag color="var(--color-primary)">Nuevo</Tag>}
              </div>
              {entry.publishedAt && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                  {formatInTimeZone(entry.publishedAt, 'DD [de] MMMM [de] YYYY')}
                </Text>
              )}
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }} ellipsis={{ rows: 3 }}>
                {entry.summary}
              </Paragraph>
              <Text style={{ color: 'var(--color-primary)', fontSize: 13, display: 'inline-block', marginTop: 8 }}>
                Ver manual completo →
              </Text>
            </div>
          </div>
        </Card>
      ))}

      <Modal
        title={selectedEntry?.title}
        open={selectedId !== null}
        onCancel={() => setSelectedId(null)}
        footer={null}
        width={isMobile ? '100vw' : 920}
        style={isMobile ? undefined : { top: 32 }}
        destroyOnClose
      >
        {detailLoading || !detail ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <iframe
            title={detail.title}
            sandbox=""
            srcDoc={buildSrcDoc(detail.htmlContent)}
            style={{
              width: '100%',
              height: isMobile ? 'calc(100vh - 120px)' : '75vh',
              border: 'none',
              borderRadius: 8,
              background: 'transparent',
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default Novedades;
